import { Button, TableItem } from 'components/atoms';
import LabelBoolen from 'components/molecules/labeledStatus/LabeledBoolen';
import LabelSignatureStatus from 'components/molecules/labeledStatus/LabeledSignature';
import { FooterTable, TopBar } from 'components/organisms/index';
import { TopbarPage } from 'components/templates';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useParamId } from 'hooks/useParamId';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PiClockCounterClockwiseBold, PiFile } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetAdvanceReport,
  useGetAdvancesByLabelInfinite,
  useGetAdvancesStatisticsByLabel,
} from 'services/advance/index';
import { REPAYMENTS_TYPE, SIGNATURE_TYPE } from 'services/advance/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { formatDate } from 'utils/global/date.utils';

const LabelAdvance = () => {
  const { t } = useTranslation();

  const labelId = useParamId('label');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setFile } = useStreamFileDownload(useGetAdvanceReport);

  const isAdvancTabInEditMode = pathname.includes('edit');
  const isAdvancTabInCreateMode = pathname.includes('create');

  useEffect(() => {
    if (isAdvancTabInEditMode) {
      navigate(`label/edit/${labelId}/finances`);
    }

    if (isAdvancTabInCreateMode) {
      navigate(`label/create/finances`);
    }
  }, [isAdvancTabInEditMode, isAdvancTabInCreateMode]);

  const advances = useGetAdvancesByLabelInfinite(labelId);
  const statistics = useGetAdvancesStatisticsByLabel(labelId);
  const tableProps = useInfiniteTable(advances);

  const allAmountLength = formatCurrency(statistics?.data?.allAmount).length;
  const remainingNetAmountLength = formatCurrency(
    statistics?.data?.allRemainingNetAmount
  ).length;

  const handleDownloadReport = () => {
    setFile({
      id: labelId,
      name: `${t('button.download-report').replaceAll(' ', '_')}_${format(
        new Date(),
        'dd_MM_yyyy_HH_mm'
      )}`,
      extension: 'xlsx',
    });
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <>
              <Button isClear onClick={handleDownloadReport}>
                <Button.Text text={t('button.download-report')} />
                <Button.Icon icon={<PiFile />} />
              </Button>
              <Button onlyIcon isClear onClick={() => navigate('history')}>
                <Button.Description description={t('history.contract')} />
                <Button.Icon icon={<PiClockCounterClockwiseBold />} />
              </Button>
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate(`/label/${labelId}/advance/create`)}>
                <Button.Text text={t('button.add')} />
              </Button>
            </>
          }
        />
      }>
      <FooterTable
        id={'advanceLabel'}
        {...tableProps}
        data={{
          nodes: tableProps.data.nodes.map(d => ({
            ...d,
            id: `${d.id}/repayments`,
          })),
        }}
        size='compact'
        displayMode
        columns={[
          {
            label: t('input.label.date'),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={formatDate(item.date)} />
              </TableItem>
            ),
            resize: true,
            footer: t('table.total'),
          },
          {
            label: t('input.label.description'),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={`${item.description}`} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.signature'),
            renderCell: item => (
              <TableItem>
                <LabelSignatureStatus
                  status={
                    item.forSignature
                      ? SIGNATURE_TYPE.true
                      : SIGNATURE_TYPE.false
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.amount'),
            renderCell: item => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.amount, {
                    length: allAmountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
            footer: formatCurrency(statistics.data?.allAmount, {
              length: allAmountLength,
            }),
          },
          {
            label: t('input.label.remainingNetAmount'),
            renderCell: item => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.remainingNetAmount, {
                    length: remainingNetAmountLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
            footer: formatCurrency(statistics.data?.allRemainingNetAmount, {
              length: remainingNetAmountLength,
            }),
          },
          {
            label: t('input.label.repaymentsFromAllContracts'),
            renderCell: (item: any) => (
              <TableItem>
                <LabelBoolen
                  status={
                    item.repaymentsFromAllContracts
                      ? REPAYMENTS_TYPE.true
                      : REPAYMENTS_TYPE.false
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default LabelAdvance;
